import { Shield, Users, Award, Star, Target, Briefcase, Mail, Compass, Globe2, Users2, Landmark, Layers, Scale } from 'lucide-react';
import PublicShell from '@/components/layouts/PublicShell';
import SplitHero from '@/components/public/SplitHero';
import SplitFeature from '@/components/public/SplitFeature';
import FeatureCard from '@/components/public/FeatureCard';
import CtaCard from '@/components/public/CtaCard';
import PhotoPanel from '@/components/public/PhotoPanel';

interface TeamMember {
  name: string;
  role: string;
  image: string;
  bio: string;
  roleColor: string;
  fallbackIcon: typeof Users;
}

const TeamCard = ({ member, size = 'md' }: { member: TeamMember; size?: 'md' | 'sm' }) => {
  const Fallback = member.fallbackIcon;
  const dim = size === 'md' ? 'w-28 h-28' : 'w-20 h-20';
  return (
    <div className="rounded-2xl border border-slate-200 bg-white hover:border-primary-300 hover:shadow-[0_12px_32px_rgba(15,23,42,0.08)] transition-all duration-200 p-7 text-center">
      <div className={`${dim} mx-auto mb-5 rounded-full overflow-hidden bg-slate-100`}>
        <img
          src={member.image}
          alt={member.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const sibling = e.currentTarget.nextElementSibling as HTMLElement | null;
            if (sibling) sibling.style.display = 'flex';
          }}
        />
        <div className="hidden w-full h-full bg-primary-50 items-center justify-center">
          <Fallback className="w-8 h-8 text-primary-300" />
        </div>
      </div>
      <h4 className="text-base font-semibold text-slate-900 mb-1">{member.name}</h4>
      <p className={`font-semibold mb-3 text-sm ${member.roleColor}`}>{member.role}</p>
      <p className="text-slate-600 text-xs leading-relaxed">{member.bio}</p>
    </div>
  );
};

const MANAGEMENT: TeamMember[] = [
  {
    name: 'Benjamin Winter',
    role: 'Geschäftsführer',
    image: '/images/benjaminwinter.jpg',
    bio: 'Über 20 Jahre Erfahrung in der Finanzbranche. Spezialist für strategische Unternehmensführung und Risikomanagement.',
    roleColor: 'text-primary-700',
    fallbackIcon: Users,
  },
  {
    name: 'Dr. Anna Schmidt',
    role: 'Geschäftsführerin',
    image: '/images/anaschmidt.jpg',
    bio: 'Expertin für nachhaltige Investments und ESG-Strategien. Führt das Unternehmen mit Fokus auf Innovation und Nachhaltigkeit.',
    roleColor: 'text-primary-700',
    fallbackIcon: Users,
  },
];

const ADVISORY: TeamMember[] = [
  {
    name: 'Thomas Becker',
    role: 'Anlageberater',
    image: '/images/thomasbecker.jpg',
    bio: 'Spezialist für konventionelle Anlagestrategien und Portfoliooptimierung.',
    roleColor: 'text-primary-600',
    fallbackIcon: Target,
  },
  {
    name: 'Claudia Vogel',
    role: 'Anlageberatung und Research',
    image: '/images/claudiavogel.jpg',
    bio: 'Expertin für Marktanalyse und fundierte Anlagestrategien.',
    roleColor: 'text-primary-600',
    fallbackIcon: Award,
  },
  {
    name: 'Stefan Hoffmann',
    role: 'Anlageberater',
    image: '/images/stefanhoffmann.jpg',
    bio: 'Fokus auf nachhaltige Investments und ESG-konforme Portfolios.',
    roleColor: 'text-primary-600',
    fallbackIcon: Shield,
  },
];

const SUPPORT: TeamMember[] = [
  {
    name: 'Julia Wagner',
    role: 'Sekretariat',
    image: '/images/juliawagner.jpg',
    bio: 'Ihre erste Ansprechpartnerin für Terminvereinbarungen und allgemeine Anfragen.',
    roleColor: 'text-gold-700',
    fallbackIcon: Mail,
  },
  {
    name: 'Michael Braun',
    role: 'Sekretariat',
    image: '/images/michaelbraun.jpg',
    bio: 'Unterstützt das Team bei administrativen Aufgaben und Kundenbetreuung.',
    roleColor: 'text-gold-700',
    fallbackIcon: Briefcase,
  },
];

const AboutPage = () => {
  return (
    <PublicShell>
      <SplitHero
        eyebrow="Über Sky View"
        title="Über uns"
        subtitle="Erfahren Sie mehr über unser Unternehmen, unsere Werte und unsere Mission."
        graphic={<PhotoPanel src="/images/sv-team-meeting.jpg" alt="Über Sky View" />}
        primaryLabel="Kontakt aufnehmen"
        primaryTo="/contact"
      />

      <SplitFeature
        icon={Shield}
        eyebrow="Verantwortung"
        title="Verantwortung ist unser Fundament"
        description="Unser Geschäft basiert auf einer Kultur der Verantwortung. Wir sind überzeugt: Verantwortungsvolles Wirtschaften führt zu langfristig erfolgreichen Geschäften. Ganz gleich, ob im Umgang mit Kunden, Gemeinden oder Mitarbeitenden – wir setzen uns für eine bessere finanzielle Vorsorge, wirtschaftliche Chancen und die Wertschätzung vielfältiger Perspektiven ein."
        graphic={<PhotoPanel src="/images/sv-office-meeting.jpg" alt="Verantwortung" />}
        tint
      />

      <section className="bg-primary-50/40 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <FeatureCard
              icon={Compass}
              title="Stewardship als Leitprinzip"
              description="Als Verwalter des Vermögens unserer Kunden übernehmen wir Verantwortung für dessen zukünftige Entwicklung. Stewardship bedeutet für uns, vorausschauend zu handeln – für bessere Anlageergebnisse und widerstandsfähigere Portfolios."
            />
            <FeatureCard
              icon={Globe2}
              title="Wirkung in der Gemeinschaft"
              description="Unser Unternehmen agiert nicht isoliert, sondern ist eingebettet – mit direktem Einfluss auf soziale, wirtschaftliche und ökologische Rahmenbedingungen. Wir investieren gezielt in wirkungsvolle Projekte."
            />
            <FeatureCard
              icon={Users2}
              title="Vielfalt stärkt uns"
              description="Wir fördern aktiv Vielfalt, Gleichberechtigung und Inklusion – nicht nur aus Überzeugung, sondern als klares Zeichen unseres Engagements für unsere Mitarbeitenden."
            />
          </div>
        </div>
      </section>

      <SplitFeature
        icon={Award}
        eyebrow="Expertise"
        title="Expertise für bessere Anlageergebnisse"
        description="Unsere Erfahrung im Risikomanagement bildet das Fundament unserer Anlagestrategie. Verantwortungsvolle Kapitalverwalter ignorieren Risiken nicht – sie managen sie. Durch unsere Expertise verfolgen wir konsequent bessere Anlage- und Vorsorgeergebnisse für unsere Kunden."
        graphic={<PhotoPanel src="/images/sv-skyline-financial.jpg" alt="Expertise" />}
        reverse
      />

      <section className="pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <FeatureCard
              icon={Landmark}
              title="Versicherungskompetenz"
              description="Unsere Wurzeln im Versicherungsgeschäft verschaffen uns einen klaren Vorteil im Umgang mit Risiken – geprägt durch intelligentes Risikomanagement und breit diversifizierte Portfolios."
            />
            <FeatureCard
              icon={Layers}
              title="Ergänzende Anlagemöglichkeiten"
              description="Investmentfonds und ETFs, individuelle und betriebliche Vorsorgepläne für Bildung und Altersvorsorge, sowie Modellportfolios und maßgeschneiderte Anlagestrategien – für jeden Bedarf."
            />
            <FeatureCard
              icon={Scale}
              title="Ganzheitliche Betreuung"
              description="Unser Leistungsangebot deckt das gesamte Spektrum finanzieller Risiken ab – von individuellen Lösungen über Gruppen- und Einzelvorsorge bis hin zur umfassenden Vermögensberatung."
            />
          </div>
        </div>
      </section>

      <SplitFeature
        icon={Star}
        eyebrow="Kultur"
        title="Werte schaffen Zukunft"
        description="Unsere werteorientierte Unternehmenskultur zieht engagierte und vielfältige Talente an – innerhalb und außerhalb des Finanzsektors. Vielfalt im Denken und in der Erfahrung ist ein zentraler Bestandteil unserer Investmentphilosophie und stärkt unsere Innovationskraft, unsere Kundenbindung und unser Team."
        graphic={<PhotoPanel src="/images/sv-group-meeting.jpg" alt="Unternehmenskultur" />}
        tint
      />

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-600 mb-3 block">Unser Team</span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-slate-900 leading-tight">Die Menschen hinter Sky View</h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
              Erfahrene Expertinnen und Experten mit Leidenschaft für Finanzdienstleistungen.
            </p>
          </div>

          <div className="mb-14">
            <h3 className="text-lg font-semibold text-slate-900 mb-6 text-center">Geschäftsführung</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {MANAGEMENT.map((m) => (
                <TeamCard key={m.name} member={m} />
              ))}
            </div>
          </div>

          <div className="mb-14">
            <h3 className="text-lg font-semibold text-slate-900 mb-6 text-center">Beratungsteam</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {ADVISORY.map((m) => (
                <TeamCard key={m.name} member={m} size="sm" />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-6 text-center">Unser Support-Team</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {SUPPORT.map((m) => (
                <TeamCard key={m.name} member={m} size="sm" />
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaCard
        title="Interesse an einer Zusammenarbeit?"
        subtitle="Entdecken Sie, wie Sie den nächsten Schritt in Ihrer Karriere bei Sky View machen können."
        primaryLabel="Offene Stellen ansehen"
        primaryTo="/stellen"
        secondaryLabel="Kontakt aufnehmen"
        secondaryTo="/contact"
      />
    </PublicShell>
  );
};

export default AboutPage;
